import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useImposter, useIsDesktop } from 'Hooks';
import {
  Avatar,
  ButtonBase,
  Link,
  MenuItem,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import {
  LinearUser,
  LinearUserOpen,
  MobileUser,
  SignOut,
  UpdatePassword,
  UpdatePersonalDetails,
  ViewDisabledVeteranCard,
  ViewPersonalDetails,
} from 'Images';
import { useState } from 'react';
import { RoutesConfig } from 'Routes';
import { DynamicArrow } from 'Components/DynamicArrow';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { b2cPolicies } from 'B2C';

export const UserAccordion = () => {
  const { isAdmin } = useImposter();
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Header);
  const [navMenuButtonAnchorEl, setNavMenuButtonAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const { instance, inProgress } = useMsal();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setNavMenuButtonAnchorEl((prevAnchor) => {
      if (!prevAnchor) {
        return event.currentTarget;
      } else {
        return null;
      }
    });
  };

  const handleClose = () => {
    setNavMenuButtonAnchorEl(null);
  };

  const open = Boolean(navMenuButtonAnchorEl);
  const navMenuId = open ? 'nav-menu-popover' : undefined;

  const menuItems = dictionary.personalButton.menuItems;

  const menuItemsWithIcon = [
    {
      label: menuItems.viewPersonalDetails.label,
      href: process.env.PUBLIC_URL + RoutesConfig.PERSONAL_DETAILS.PATH,
      icon: ViewPersonalDetails,
    },
    {
      label: menuItems.updatePersonalDetails.label,
      href: process.env.PUBLIC_URL + '/requests/update-personal-details/8/18',
      icon: UpdatePersonalDetails,
    },
    {
      label: menuItems.viewDisabledVeteranCertificate.label,
      href: RoutesConfig.DISABLED_VETERAN.PATH,
      icon: ViewDisabledVeteranCard,
    },
    {
      label: menuItems.updatePassword.label,
      onClick: () => {
        const xToken = sessionStorage.getItem('B2CToken');
        if (xToken && inProgress === InteractionStatus.None) {
          instance.loginRedirect({
            ...b2cPolicies.authorities.editProfile,
            extraQueryParameters: {
              xToken,
            },
          });
        }
      },
      icon: UpdatePassword,
    },
    {
      label: menuItems.signout.label,
      onClick: async () => {
        if (inProgress === InteractionStatus.None) {
          sessionStorage.removeItem('B2CToken');
          sessionStorage.removeItem('token');
          isAdmin
            ? (document.location = process.env.REACT_APP_DISCONNECT_URL || '')
            : await instance.logoutRedirect();
        }
      },
      icon: SignOut,
    },
  ];

  return (
    <>
      {isDesktop ? (
        <ButtonBase
          aria-describedby={navMenuId}
          onClick={handleClick}
          sx={{
            bgcolor: 'primary.main',
            width: '198px',
            height: '48px',
            borderRadius: '8px',
            zIndex: '2002',
          }}
        >
          <Stack direction={'row'} justifyContent={'center'} gap={'8px'}>
            <Avatar
              alt={''}
              src={LinearUser}
              sx={{ width: '28px', height: '28px' }}
            />
            <Typography variant={'body1'} color={'#fff'}>
              {dictionary.personalButton.title}
            </Typography>
            <DynamicArrow color={'#fff'} size={28} rotate={open ? 180 : 0} />
          </Stack>
        </ButtonBase>
      ) : (
        <ButtonBase aria-describedby={navMenuId} onClick={handleClick}>
          <Avatar
            alt={''}
            src={open ? LinearUserOpen : MobileUser}
            sx={{ width: '30px', height: '30px' }}
          />
        </ButtonBase>
      )}
      <Popover
        disablePortal={true}
        id={navMenuId}
        open={open}
        anchorEl={navMenuButtonAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          zIndex: 2001,
          right: isDesktop ? undefined : '-2px',
          top: isDesktop ? '-15px' : '-1px',
        }}
        PaperProps={{
          sx: {
            borderRadius: '10px 0 10px 10px',
            boxShadow: '0',
            bgcolor: 'transparent',
          },
        }}
      >
        <Stack
          component={'ul'}
          sx={{ padding: '10px 0', margin: 0, width: '198px' }}
          bgcolor={'primary.main'}
        >
          {menuItemsWithIcon.map((item, index) => (
            <Link
              key={index}
              underline={'none'}
              color={'#fff'}
              href={item.href}
              onClick={item.onClick}
            >
              <MenuItem
                sx={{
                  padding: '8px 16px',
                  gap: '4px',
                  '&:hover': { backgroundColor: 'primary.shade_600' },
                }}
              >
                <Avatar
                  alt={''}
                  src={item.icon}
                  sx={{ width: '24px', height: '24px' }}
                />
                <Typography variant={'body3'} color={'#fff'}>
                  {item.label}
                </Typography>
              </MenuItem>
            </Link>
          ))}
        </Stack>
      </Popover>
    </>
  );
};
